import { mergeTooltipActions } from '../../../../core/components/series-action/utils/mergeTooltipActions.js';
import { getFormatter } from '../../../../core/utils/get-formatter.js';
import { safeGet } from '../../../../core/utils/safe-get.js';
import { useRectData } from '../../../hooks/useRectData.js';
import { useXYChartColorScales } from '../../../hooks/useXYChartColorScales.js';
import { useXYChartTruncationMode } from '../../../hooks/useXYChartTruncationMode.js';
import type {
  RectDatapointInternal,
  RectSeriesWithDatapoints,
} from '../../../types/xy-chart-internals.js';
import type { RectDatapoint } from '../../../types/xy-chart.js';
import { getColorFromScaleAndData } from '../../../utils/get-color-from-scale-and-data.js';
import { XYChartTooltipHeader } from '../XYChartTooltipHeader/XYChartTooltipHeader.js';

export interface RectTooltipHeaderProps {
  datapoint: RectDatapointInternal;
  series: RectSeriesWithDatapoints;
}

export function RectSeriesTooltipHeader({
  datapoint,
  series,
}: RectTooltipHeaderProps) {
  const colorScaleMap = useXYChartColorScales();
  const { seriesToMetadata } = useRectData();
  const { truncationMode } = useXYChartTruncationMode();

  const {
    actions: seriesActions,
    valueFormatter,
    valueUnit = undefined,
    valueAccessor,
    valueAccessorLabel,
  } = safeGet(seriesToMetadata, series);
  const colorScale = safeGet(colorScaleMap, series.id);

  const { value } = datapoint;
  const actions = () => seriesActions?.({ ...datapoint } as RectDatapoint);

  const content = {
    id: crypto.randomUUID(),
    name: valueAccessorLabel ?? valueAccessor,
  };

  const valueToDisplay =
    typeof value === 'number'
      ? getFormatter(valueFormatter)(value, valueUnit)
      : value;
  // At this point we know value can not be null, undefined nor NaN, given we will never be able to hover a gap
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const valueColor = getColorFromScaleAndData(colorScale, value!);

  return (
    <XYChartTooltipHeader
      color={valueColor}
      name={content.name}
      // At this point we know value can not be null, undefined nor NaN, given we will never be able to hover a gap
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      value={valueToDisplay!}
      actions={mergeTooltipActions(content, actions)}
      truncationMode={truncationMode}
    />
  );
}

RectSeriesTooltipHeader['displayName'] = 'RectSeriesTooltipHeader';
